import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import AsyncStorage from '@react-native-async-storage/async-storage';

const HAS_SEEN_MAP_HINT_KEY = 'has_seen_map_click_hint';

type MapClickHintProps = {
  onDismiss?: () => void;
};

export const MapClickHint: React.FC<MapClickHintProps> = ({ onDismiss }) => {
  const theme = useTheme();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const checkAndShowHint = async () => {
      try {
        const hasSeenHint = await AsyncStorage.getItem(HAS_SEEN_MAP_HINT_KEY);
        if (!hasSeenHint) {
          // Small delay to ensure map is loaded
          setTimeout(() => {
            setVisible(true);
          }, 1000);
        }
      } catch (error) {
        console.error('[MapClickHint] Error checking hint status:', error);
      }
    };

    checkAndShowHint();
  }, []);

  const handleDismiss = async () => {
    try {
      await AsyncStorage.setItem(HAS_SEEN_MAP_HINT_KEY, 'true');
      setVisible(false);
      onDismiss?.();
    } catch (error) {
      console.error('[MapClickHint] Error saving hint status:', error);
      setVisible(false);
      onDismiss?.();
    }
  };

  if (!visible) return null;

  return (
    <View style={styles.container}>
      <Card
        style={[
          styles.hintCard,
          {
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.primary + '30',
            ...(Platform.OS === 'web' ? {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            } : {
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 8,
              elevation: 5,
            }),
          },
        ]}
      >
        <View style={styles.hintContent}>
          <View style={[styles.iconContainer, { backgroundColor: theme.colors.primary + '15' }]}>
            <Ionicons name="hand-left-outline" size={24} color={theme.colors.primary} />
          </View>
          <View style={styles.textContainer}>
            <Body style={[styles.title, { color: theme.colors.text }]}>
              Tap anywhere on the map
            </Body>
            <Caption color={theme.colors.textSecondary} style={styles.description}>
              Click on the map to add places, landmarks, or reference points to your trip
            </Caption>
          </View>
          <Pressable
            onPress={handleDismiss}
            style={({ pressed }) => [
              styles.closeButton,
              { opacity: pressed ? 0.7 : 1 },
            ]}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="close" size={20} color={theme.colors.textSecondary} />
          </Pressable>
        </View>
      </Card>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: Platform.OS === 'web' ? 80 : 100,
    left: 16,
    right: 16,
    zIndex: 1001,
    pointerEvents: 'box-none',
  },
  hintCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderLeftWidth: 4,
  },
  hintContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  title: {
    fontWeight: '600',
    fontSize: 15,
  },
  description: {
    fontSize: 13,
    lineHeight: 18,
  },
  closeButton: {
    padding: 4,
    flexShrink: 0,
  },
});

